import React, { useState, useEffect } from "react";
import {
  LineChart,
  BarChart,
  PieChart,
  Line,
  Bar,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
  Text,
} from "recharts";
import { useLocation } from "react-router-dom";
import { fetchGraphicalReportsData } from "../apis/reports";
import { displayToast } from "../utils/Toast";

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    padding: "48px 24px",
  },
  card: {
    maxWidth: "1200px",
    margin: "0 auto",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(to right, #4f46e5, #818cf8)",
    padding: "24px 32px",
    color: "#ffffff",
  },
  title: {
    fontSize: "30px",
    fontWeight: "bold",
    margin: "0",
  },
  subtitle: {
    fontSize: "16px",
    marginTop: "8px",
    color: "rgba(255, 255, 255, 0.9)",
  },
  content: {
    padding: "32px",
  },
  selectContainer: {
    marginBottom: "32px",
  },
  select: {
    width: "100%",
    maxWidth: "400px",
    padding: "12px 16px",
    fontSize: "16px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    outline: "none",
    transition: "all 0.2s ease-in-out",
  },
  chartContainer: {
    backgroundColor: "#ffffff",
    padding: "24px",
    borderRadius: "12px",
    border: "1px solid #f3f4f6",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    minHeight: "400px",
  },
  description: {
    textAlign: "center",
    marginTop: "24px",
  },
  descriptionText: {
    display: "inline-block",
    fontSize: "14px",
    color: "#4b5563",
    backgroundColor: "#f9fafb",
    padding: "8px 16px",
    borderRadius: "8px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "400px",
    color: "#6B7280",
  },
  errorMessage: {
    textAlign: "center",
    color: "#EF4444",
    padding: "16px",
    fontSize: "14px",
    backgroundColor: "#FEF2F2",
    borderRadius: "8px",
    margin: "0 0 16px 0",
  },
  noDataContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "400px",
    color: "#6B7280",
  },
  noDataIcon: {
    fontSize: "48px",
    marginBottom: "16px",
    color: "#9CA3AF",
  },
  noDataText: {
    fontSize: "18px",
    fontWeight: "medium",
    color: "#4B5563",
    textAlign: "center",
    maxWidth: "400px",
  },
  noDataSubtext: {
    fontSize: "14px",
    color: "#6B7280",
    marginTop: "8px",
    textAlign: "center",
  },
};

const colorMap = {
  Jan: "#4F46E5",
  Feb: "#10B981",
  Mar: "#F59E0B",
  Apr: "#EC4899",
  May: "#6366F1",
  Jun: "#8B5CF6",
  Jul: "#2563EB",
  Aug: "#DC2626",
  Sep: "#D97706",
  Oct: "#059669",
  Nov: "#7C3AED",
  Dec: "#DB2777",
};

const formatLargeNumber = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

const getDummyData = (chartType) => {
  const emptyData = [
    { name: "Jan", sales: 0, value: 0 },
    { name: "Feb", sales: 0, value: 0 },
    { name: "Mar", sales: 0, value: 0 },
    { name: "Apr", sales: 0, value: 0 },
  ];

  return emptyData;
};

const NoDataOverlay = () => (
  <div style={styles.noDataContainer}>
    <div style={styles.noDataIcon}>📊</div>
    <div style={styles.noDataText}>No Data Found</div>
    <div style={styles.noDataSubtext}>
      There is no data available for the selected parameters.
    </div>
  </div>
);

const Charts = () => {
  const [chartType, setChartType] = useState("bar");
  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState({
    xAxis: "Period",
    yAxis: "Value",
    zAxis: "Secondary Value",
  });
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const reportsDataQuery = location.state?.reportsDataQuery;

  const processApiResponse = (response) => {
    if (response && response.data && Array.isArray(response.data)) {
      if (response.label) {
        setChartLabels({
          xAxis: response.label["x-axis"],
          yAxis: response.label["y-axis"],
          zAxis: response.label["z-axis"],
        });
      }

      return response.data.map((item) => ({
        name: item.x_axis || item.month || "Unknown",
        sales: parseFloat(item.y_axis) || 0,
        value: parseFloat(item.z_axis) || 0,
      }));
    }

    return [];
  };

  const fetchChartData = async () => {
    try {
      setLoading(true);
      const response = await fetchGraphicalReportsData(reportsDataQuery);

      if (
        response &&
        (response.data || (Array.isArray(response) && response.length > 0))
      ) {
        const processedData = processApiResponse(response);

        if (processedData.length > 0) {
          setChartData(processedData);
        }
      } else if (response && response.description) {
        displayToast(response.description);
      }
    } catch (err) {
      displayToast(err.message || "Failed to fetch chart data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [reportsDataQuery]);

  const getMaxYValue = () => {
    if (chartData.length === 0) return 1000;
    const maxSales = Math.max(...chartData.map((item) => item.sales || 0));
    const maxValue = Math.max(...chartData.map((item) => item.value || 0));
    const overallMax = Math.max(maxSales, maxValue);

    return Math.ceil(overallMax * 1.2);
  };

  const renderNoDataChart = () => {
    const dummyData = getDummyData(chartType);
    const tooltipStyle = {
      backgroundColor: "white",
      borderRadius: "6px",
      border: "1px solid #e5e7eb",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    };

    switch (chartType) {
      case "bar":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={dummyData}
              margin={{ top: 10, right: 30, left: 55, bottom: 30 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E5E7EB"
                opacity={0.3}
              />
              <XAxis
                dataKey="name"
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14, opacity: 0.3 }}
                tickLine={{ stroke: "#6B7280", opacity: 0.3 }}
                axisLine={{ stroke: "#6B7280", opacity: 0.3 }}
                label={{
                  value: chartLabels.xAxis,
                  position: "insideBottom",
                  offset: -5,
                  fill: "#6B7280",
                  opacity: 0.3,
                }}
                height={50}
              />
              <YAxis
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14, opacity: 0.3 }}
                tickLine={{ stroke: "#6B7280", opacity: 0.3 }}
                axisLine={{ stroke: "#6B7280", opacity: 0.3 }}
                domain={[0, 1000]}
                allowDecimals={false}
                label={{
                  value: chartLabels.yAxis,
                  angle: -90,
                  position: "insideLeft",
                  offset: -45,
                  fill: "#6B7280",
                  opacity: 0.3,
                }}
                width={55}
              />
              <Bar
                name={chartLabels.yAxis}
                dataKey="sales"
                fill="#E5E7EB"
                radius={[4, 4, 0, 0]}
                barSize={60}
              />
              <Text
                x="50%"
                y="50%"
                textAnchor="middle"
                verticalAnchor="middle"
                className="recharts-text"
              >
                No Data Found
              </Text>
            </BarChart>
          </ResponsiveContainer>
        );

      case "line":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={dummyData}
              margin={{ top: 10, right: 30, left: 55, bottom: 30 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E5E7EB"
                opacity={0.3}
              />
              <XAxis
                dataKey="name"
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14, opacity: 0.3 }}
                tickLine={{ stroke: "#6B7280", opacity: 0.3 }}
                axisLine={{ stroke: "#6B7280", opacity: 0.3 }}
                label={{
                  value: chartLabels.xAxis,
                  position: "insideBottom",
                  offset: -5,
                  fill: "#6B7280",
                  opacity: 0.3,
                }}
                height={50}
              />
              <YAxis
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14, opacity: 0.3 }}
                tickLine={{ stroke: "#6B7280", opacity: 0.3 }}
                axisLine={{ stroke: "#6B7280", opacity: 0.3 }}
                domain={[0, 1000]}
                label={{
                  value: chartLabels.yAxis,
                  angle: -90,
                  position: "insideLeft",
                  offset: -45,
                  fill: "#6B7280",
                  opacity: 0.3,
                }}
                width={55}
              />
              <Line
                type="monotone"
                name={chartLabels.yAxis}
                dataKey="sales"
                stroke="#E5E7EB"
                strokeWidth={2}
                dot={false}
              />
              <Text
                x="50%"
                y="50%"
                textAnchor="middle"
                verticalAnchor="middle"
                className="recharts-text"
              >
                No Data Found
              </Text>
            </LineChart>
          </ResponsiveContainer>
        );

      case "pie":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
              <Pie
                data={[{ name: "No Data", value: 1 }]}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={150}
                innerRadius={80}
                fill="#E5E7EB"
              />
              <Text
                x="50%"
                y="50%"
                textAnchor="middle"
                verticalAnchor="middle"
                className="recharts-text"
              >
                No Data Found
              </Text>
            </PieChart>
          </ResponsiveContainer>
        );

      case "stacked":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={dummyData}
              margin={{ top: 10, right: 30, left: 55, bottom: 30 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E5E7EB"
                opacity={0.3}
              />
              <XAxis
                dataKey="name"
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14, opacity: 0.3 }}
                tickLine={{ stroke: "#6B7280", opacity: 0.3 }}
                axisLine={{ stroke: "#6B7280", opacity: 0.3 }}
                label={{
                  value: chartLabels.xAxis,
                  position: "insideBottom",
                  offset: -5,
                  fill: "#6B7280",
                  opacity: 0.3,
                }}
                height={50}
              />
              <YAxis
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14, opacity: 0.3 }}
                tickLine={{ stroke: "#6B7280", opacity: 0.3 }}
                axisLine={{ stroke: "#6B7280", opacity: 0.3 }}
                domain={[0, 1000]}
                label={{
                  value: chartLabels.yAxis,
                  angle: -90,
                  position: "insideLeft",
                  offset: -45,
                  fill: "#6B7280",
                  opacity: 0.3,
                }}
                width={55}
              />
              <Bar
                dataKey="sales"
                name={chartLabels.yAxis}
                stackId="a"
                fill="#E5E7EB"
                radius={[4, 4, 0, 0]}
                barSize={40}
              />
              <Bar
                dataKey="value"
                name={chartLabels.zAxis}
                stackId="a"
                fill="#F3F4F6"
                radius={[4, 4, 0, 0]}
                barSize={40}
              />
              <Text
                x="50%"
                y="50%"
                textAnchor="middle"
                verticalAnchor="middle"
                className="recharts-text"
              >
                No Data Found
              </Text>
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  const renderChart = () => {
    const tooltipStyle = {
      backgroundColor: "white",
      borderRadius: "6px",
      border: "1px solid #e5e7eb",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    };

    if (loading) {
      return (
        <div style={styles.loadingContainer}>
          <p>Loading chart data...</p>
        </div>
      );
    }

    if (chartData.length === 0) {
      return (
        <div style={{ position: "relative", height: "400px" }}>
          {renderNoDataChart()}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            <NoDataOverlay />
          </div>
        </div>
      );
    }

    const maxYValue = getMaxYValue();

    switch (chartType) {
      case "bar":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 55, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="name"
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14 }}
                tickLine={{ stroke: "#6B7280" }}
                axisLine={{ stroke: "#6B7280" }}
                label={{
                  value: chartLabels.xAxis,
                  position: "insideBottom",
                  offset: -5,
                }}
                height={50}
              />
              <YAxis
                stroke="#6B7280"
                tickFormatter={(value) => formatLargeNumber(value)}
                tick={{ fill: "#6B7280", fontSize: 14 }}
                tickLine={{ stroke: "#6B7280" }}
                axisLine={{ stroke: "#6B7280" }}
                domain={[0, maxYValue]}
                allowDecimals={false}
                label={{
                  value: chartLabels.yAxis,
                  angle: -90,
                  position: "insideLeft",
                  offset: -45,
                }}
                width={55}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value) => [
                  value.toLocaleString(),
                  chartLabels.yAxis,
                ]}
                labelStyle={{ fontWeight: "bold", color: "#4F46E5" }}
              />
              <Legend wrapperStyle={{ paddingTop: "16px" }} />
              <Bar
                name={chartLabels.yAxis}
                dataKey="sales"
                fill="#4F46E5"
                radius={[4, 4, 0, 0]}
                barSize={60}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colorMap[entry.name] || "#4F46E5"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case "line":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 55, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="name"
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14 }}
                tickLine={{ stroke: "#6B7280" }}
                label={{
                  value: chartLabels.xAxis,
                  position: "insideBottom",
                  offset: -5,
                }}
                height={50}
              />
              <YAxis
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14 }}
                tickLine={{ stroke: "#6B7280" }}
                domain={[0, maxYValue]}
                tickFormatter={(value) => formatLargeNumber(value)}
                label={{
                  value: chartLabels.yAxis,
                  angle: -90,
                  position: "insideLeft",
                  offset: -45,
                }}
                width={55}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value) => [
                  value.toLocaleString(),
                  chartLabels.yAxis,
                ]}
              />
              <Legend wrapperStyle={{ paddingTop: "16px" }} />
              <Line
                type="monotone"
                name={chartLabels.yAxis}
                dataKey="sales"
                stroke="#4F46E5"
                strokeWidth={2}
                activeDot={{ r: 8 }}
              />
              {chartData[0]?.value !== undefined && (
                <Line
                  type="monotone"
                  name={chartLabels.zAxis}
                  dataKey="value"
                  stroke="#10B981"
                  strokeWidth={2}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        );

      case "pie":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
              <Pie
                data={chartData}
                dataKey="sales"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={150}
                innerRadius={80}
                paddingAngle={2}
                label={({
                  cx,
                  cy,
                  midAngle,
                  innerRadius,
                  outerRadius,
                  value,
                  name,
                }) => {
                  const RADIAN = Math.PI / 180;
                  const radius = 25 + innerRadius + (outerRadius - innerRadius);
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);

                  return (
                    <text
                      x={x}
                      y={y}
                      style={{ fontSize: "14px", fontWeight: "bold" }}
                      textAnchor={x > cx ? "start" : "end"}
                      dominantBaseline="central"
                    >
                      {`${name} (${formatLargeNumber(value)})`}
                    </text>
                  );
                }}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colorMap[entry.name] || "#4F46E5"}
                    stroke="#fff"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value) => [
                  formatLargeNumber(value),
                  chartLabels.yAxis,
                ]}
              />
              <Legend
                wrapperStyle={{ paddingTop: "32px" }}
                layout="horizontal"
                align="center"
              />
            </PieChart>
          </ResponsiveContainer>
        );

      case "stacked":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 55, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="name"
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14 }}
                label={{
                  value: chartLabels.xAxis,
                  position: "insideBottom",
                  offset: -5,
                }}
                height={50}
              />
              <YAxis
                stroke="#6B7280"
                tick={{ fill: "#6B7280", fontSize: 14 }}
                domain={[0, maxYValue]}
                tickFormatter={(value) => formatLargeNumber(value)}
                label={{
                  value: chartLabels.yAxis,
                  angle: -90,
                  position: "insideLeft",
                  offset: -45,
                }}
                width={55}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value) => [value.toLocaleString()]}
              />
              <Legend wrapperStyle={{ paddingTop: "16px" }} />
              <Bar
                dataKey="sales"
                name={chartLabels.yAxis}
                stackId="a"
                fill="#4F46E5"
                radius={[4, 4, 0, 0]}
                barSize={40}
              />
              {chartData[0]?.value !== undefined && (
                <Bar
                  dataKey="value"
                  name={chartLabels.zAxis}
                  stackId="a"
                  fill="#10B981"
                  radius={[4, 4, 0, 0]}
                  barSize={40}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>ERP Management Charts</h2>
          <p style={styles.subtitle}>Analytics and Insights</p>
        </div>
        <div style={styles.content}>
          <div style={styles.selectContainer}>
            <select
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              style={styles.select}
              disabled={loading}
            >
              <option value="bar">Bar Chart</option>
              <option value="line">Line Chart</option>
              <option value="pie">Pie Chart</option>
              <option value="stacked">Stacked Bar Chart</option>
            </select>
          </div>

          <div style={styles.chartContainer}>{renderChart()}</div>

          <div style={styles.description}>
            <p style={styles.descriptionText}>
              {chartType === "bar" &&
                `${chartLabels.xAxis} vs ${chartLabels.yAxis} comparison using bar chart visualization`}
              {chartType === "line" &&
                `${chartLabels.yAxis} trends over ${chartLabels.xAxis} periods using line chart visualization`}
              {chartType === "pie" &&
                `${chartLabels.yAxis} distribution by ${chartLabels.xAxis} using pie chart visualization`}
              {chartType === "stacked" &&
                `Stacked view of ${chartLabels.yAxis} and ${chartLabels.zAxis} by ${chartLabels.xAxis}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Charts;
